// Format a Shopify price object as ₹-prefixed, thousands-grouped, no decimals
// for round numbers — matches the visual reference (₹4,299 not ₹4,299.00).
export function formatINR(price) {
  if (!price || price.amount == null) return null;
  const amount = Number(price.amount);
  if (Number.isNaN(amount)) return null;
  const symbol = price.currencyCode === "INR" ? "₹" : "";
  const hasDecimal = amount % 1 !== 0;
  const formatted = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: hasDecimal ? 2 : 0,
  }).format(amount);
  return `${symbol}${formatted}`;
}

// Derive the type-aware spec eyebrow tag for a product card.
// KEYBOARD → layout+tech (e.g. "65% · HALL EFFECT")
// MOUSE    → polling+weight (e.g. "8K · 49G")
// MOUSEPAD → glide+material (e.g. "SPEED · GLASS")
// OTHER    → most specific tag
export function deriveEyebrow(product) {
  const type = String(product.productType || "").toLowerCase();
  const tags = (product.tags || []).map((t) => String(t).toLowerCase());
  const title = String(product.title || "").toLowerCase();

  const pick = (candidates) => candidates.find((c) => c) || null;

  if (type.includes("keyboard") || type.includes("pcb") || type.includes("module")) {
    const layout = pick([
      tags.find((t) => /^\d{2,3}-percent$/.test(t))?.replace("-percent", "%"),
      /(\d{2,3})%/.exec(title)?.[1] ? `${/(\d{2,3})%/.exec(title)[1]}%` : null,
      tags.includes("tkl") ? "TKL" : null,
      tags.includes("full-size") ? "FULL" : null,
    ]);
    const tech = pick([
      tags.includes("he") || tags.includes("hall-effect") ? "HALL EFFECT" : null,
      tags.includes("optical") ? "OPTICAL" : null,
      tags.includes("mechanical") ? "MECHANICAL" : null,
      title.includes("hall") ? "HALL EFFECT" : null,
    ]);
    return [layout, tech].filter(Boolean).join(" · ") || tags[0]?.toUpperCase() || null;
  }

  if (type.includes("mouse") && !type.includes("pad")) {
    const polling = pick([
      tags.find((t) => /^\d+k$/.test(t))?.toUpperCase(),
      /(\d+)k/i.exec(title)?.[0]?.toUpperCase(),
    ]);
    const weight = pick([
      tags.find((t) => /^\d{2,3}g$/.test(t))?.toUpperCase(),
      /(\d{2,3})g/i.exec(title)?.[0]?.toUpperCase(),
    ]);
    return [polling, weight].filter(Boolean).join(" · ") || (tags[0]?.toUpperCase() ?? null);
  }

  if (type.includes("mousepad") || type.includes("pad") || title.includes("mousepad")) {
    const glide = pick([
      tags.includes("speed") ? "SPEED" : null,
      tags.includes("control") ? "CONTROL" : null,
      tags.includes("balanced") || title.includes("balanced") ? "BALANCED" : null,
    ]);
    const material = pick([
      tags.includes("glass") ? "GLASS" : null,
      tags.includes("cloth") ? "CLOTH" : null,
      tags.includes("hybrid") ? "HYBRID" : null,
    ]);
    return [glide, material].filter(Boolean).join(" · ") || product.productType?.toUpperCase() || null;
  }

  // OTHER — pick the most specific tag or fall back to productType.
  const brandTag = tags.find((t) => !t.startsWith("brand:"));
  return (brandTag || product.productType || tags[0] || "").toUpperCase() || null;
}

// Deterministic per-product gradient placeholder for images that are missing.
// Two hues derived from the product handle; no external requests, no picsum.
export function placeholderGradient(handle = "loadout") {
  let h = 0;
  for (let i = 0; i < handle.length; i++) h = (h * 31 + handle.charCodeAt(i)) % 360;
  const a = h;
  const b = (h + 47) % 360;
  return `linear-gradient(135deg, hsl(${a}, 12%, 14%) 0%, hsl(${b}, 8%, 22%) 100%)`;
}

export function firstLetter(text = "?") {
  const t = String(text).trim();
  return t.length ? t[0].toUpperCase() : "?";
}
